import { useEffect, useRef } from "react";
import { getFormDefaultValues } from "../../ElementInspectorV3/utils";
import { ALL_ELEMENTS_GROUP_LABEL } from "./constants";
import type { GroupedSmartSurveyorElement } from "components/SmartSurveyor/ReviewPage/types";
import type { PlanV3 } from "../../utils/types";
import type { GroupSelectionV3Form } from "./useGroupSelectionV3";

interface UseResetOnPlanChangeArgs {
  selectedPlan: PlanV3 | null;
  isInsideRoom: boolean;
  allPlanElements: GroupedSmartSurveyorElement[];
  resetToIdle: () => void;
  setSelectedGroupLabel: (label: string | null) => void;
  setSelectedElementId: (id: string | null) => void;
  selectedElementForm: GroupSelectionV3Form;
}

export const useResetOnPlanChange = ({
  selectedPlan,
  isInsideRoom,
  allPlanElements,
  resetToIdle,
  setSelectedGroupLabel,
  setSelectedElementId,
  selectedElementForm,
}: UseResetOnPlanChangeArgs) => {
  const selectedFloorPlanId = selectedPlan?.floorPlanId;
  const prevFloorPlanIdRef = useRef(selectedFloorPlanId);
  const prevIsInsideRoomRef = useRef(isInsideRoom);

  useEffect(() => {
    const hasPlanChanged = selectedFloorPlanId !== prevFloorPlanIdRef.current;
    const hasRoomChanged = isInsideRoom !== prevIsInsideRoomRef.current;
    if (!hasPlanChanged && !hasRoomChanged) return;
    // A plan change empties the rooms in the same render, so both flip together and reset once
    prevFloorPlanIdRef.current = selectedFloorPlanId;
    prevIsInsideRoomRef.current = isInsideRoom;
    if (!selectedPlan) return;

    resetToIdle();
    setSelectedGroupLabel(ALL_ELEMENTS_GROUP_LABEL);
    const firstElement = allPlanElements[0] ?? null;
    setSelectedElementId(firstElement?.id ?? null);
    selectedElementForm.reset(getFormDefaultValues(firstElement));
  }, [
    selectedFloorPlanId,
    selectedPlan,
    isInsideRoom,
    allPlanElements,
    resetToIdle,
    setSelectedGroupLabel,
    setSelectedElementId,
    selectedElementForm,
  ]);
};
